import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Bed, Bath, MapPin, Check, CalendarDays, KeyRound, ChevronLeft, ChevronRight, ZoomIn, Play, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import type { Property, SiteSettings } from '../types';
import { formatPrice, formatDate } from '../lib/format';
import Lightbox from '../components/Lightbox';
import PropertyCard from '../components/PropertyCard';
import { Spinner, Alert } from '../components/ui';

export default function PropertyDetails() {
  const { id, code } = useParams();
  const propertyKey = code || id;
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeImage, setActiveImage] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [enquiry, setEnquiry] = useState({ name: '', email: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState<SiteSettings>({});

  useEffect(() => {
    api.get<SiteSettings>('/settings').then(setSettings).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get<Property>(`/properties/${propertyKey}`)
      .then(setProperty)
      .catch(() => setError('Property not found'))
      .finally(() => setLoading(false));
  }, [propertyKey]);

  if (loading) return <div className="pt-32"><Spinner label="Loading property..." /></div>;
  if (error || !property) {
    return (
      <div className="pt-32 max-w-3xl mx-auto px-4">
        <Alert>{error || 'Property not found'}</Alert>
      </div>
    );
  }

  const images = (property.images?.length ? property.images : [property.main_image]).filter((x): x is string => !!x);
  const media = [
    ...images.map((url) => ({ type: 'image' as const, url })),
    ...(property.videos || []).map((url) => ({ type: 'video' as const, url })),
  ];
  const lightboxItems = media.map((m) => ({ type: m.type, url: m.url, label: property.title }));
  const location = [property.area, property.location, property.state].filter(Boolean).join(', ');
  const sectionHref = property.listing_type === 'management' ? '/management' : property.listing_type === 'sale' ? '/sales' : '/leasing';
  const sectionLabel = property.listing_type === 'management' ? 'Management' : property.listing_type === 'sale' ? 'Sales' : 'Leasing';

  const handleEnquiry = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/dashboard/contact', {
        ...enquiry,
        agent_email: settings.email || '',
        message: `${enquiry.message}\n\nInterested in property: ${property.title} (${property.property_code}) - ${property.main_image}`,
      });
      setSent(true);
    } catch {
      // ignore
    } finally {
      setSending(false);
    }
  };

  const badge =
    property.listing_type === 'sale' ? 'For Sale'
      : property.listing_type === 'rent' ? 'For Rent'
        : property.listing_type === 'lease' ? 'For Lease'
          : 'Under Management';

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[50vh] overflow-hidden">
        {images[0] && (
          <img
            src={images[0]}
            alt={property.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative max-w-7xl mx-auto px-4 h-full flex items-center pt-32 md:pt-40">
          <div className="text-white">
            <p className="uppercase tracking-[0.35em] text-primary text-xs md:text-sm font-semibold mb-3">{badge}</p>
            <h1 className="text-4xl md:text-5xl font-semibold leading-tight capitalize">{property.title}</h1>
            {location && (
              <p className="mt-3 flex items-center text-sm md:text-base uppercase tracking-widest text-ash-light">
                <MapPin className="w-4 h-4 mr-2 text-primary" /> {location}
              </p>
            )}
          </div>
        </div>
      </section>

    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Breadcrumb / Code */}
        <div className="mb-6 flex items-center justify-between">
          <Link to={sectionHref} className="text-sm text-obsidian font-semibold hover:underline flex items-center">
            <ArrowRight className="h-4 w-4 mr-1 rotate-180" /> Back to {sectionLabel}
          </Link>
          <p className="text-sm text-taupe">
            Property Code: <span className="font-semibold text-obsidian">{property.property_code}</span>
          </p>
        </div>

        {/* At a glance */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-4 rounded-lg shadow border-t-2 border-primary">
            <p className="text-xs uppercase tracking-widest text-taupe mb-1">Property Code</p>
            <p className="font-semibold text-obsidian">{property.property_code}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-t-2 border-primary">
            <p className="text-xs uppercase tracking-widest text-taupe mb-1">Type</p>
            <p className="font-semibold text-obsidian capitalize">{property.property_type}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-t-2 border-primary">
            <p className="text-xs uppercase tracking-widest text-taupe mb-1">Listing</p>
            <p className="font-semibold text-obsidian capitalize">{badge}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-t-2 border-primary">
            <p className="text-xs uppercase tracking-widest text-taupe mb-1">Location</p>
            <p className="font-semibold text-obsidian truncate">{location}</p>
          </div>
        </div>

        {/* Property Media */}
        {media.length > 0 && (
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-semibold text-obsidian mb-4 capitalize">Property Media</h2>
          <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 h-72 sm:h-80 md:h-96 relative rounded-lg overflow-hidden bg-gray-100">
            {media[activeImage].type === 'video' ? (
              <video
                key={media[activeImage].url}
                controls
                muted
                autoPlay
                loop
                playsInline
                className="absolute inset-0 w-full h-full object-cover cursor-zoom-in"
                src={media[activeImage].url}
                onClick={() => setLightbox(true)}
              />
            ) : (
              <img
                src={media[activeImage].url}
                alt={property.title}
                className="absolute inset-0 w-full h-full object-cover cursor-zoom-in"
                onClick={() => setLightbox(true)}
              />
            )}
            {media.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setActiveImage((activeImage - 1 + media.length) % media.length); }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                  aria-label="Previous media"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setActiveImage((activeImage + 1) % media.length); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                  aria-label="Next media"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                  {activeImage + 1} / {media.length}
                </span>
              </>
            )}
            {media[activeImage].type === 'image' && (
              <span className="absolute bottom-3 left-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                <ZoomIn className="h-3.5 w-3.5" /> Click to view fullscreen
              </span>
            )}
            {media[activeImage].type === 'video' && (
              <span className="absolute bottom-3 left-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                <Play className="h-3.5 w-3.5" /> Click to view fullscreen
              </span>
            )}
            {property.is_sold && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <span className="bg-red-600 text-white px-8 py-3 rounded-lg text-2xl font-bold tracking-wider">SOLD</span>
              </div>
            )}
          </div>
          <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible">
            {media.map((m, i) => (
              <button
                key={i}
                onClick={() => {
                  setActiveImage(i);
                  setLightbox(true);
                }}
                className={`relative shrink-0 h-20 w-24 md:w-full md:h-20 rounded-lg overflow-hidden focus:outline-none ${i === activeImage ? 'ring-2 ring-primary' : 'opacity-80 hover:opacity-100'}`}
                aria-label={`View ${m.type === 'video' ? 'video' : 'image'} ${i + 1}`}
              >
                {m.type === 'video' ? (
                  <>
                    <video muted preload="metadata" src={m.url} className="absolute inset-0 w-full h-full object-cover" />
                    <span className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <Play className="h-6 w-6 text-white fill-current" />
                    </span>
                  </>
                ) : (
                  <img
                    src={m.url}
                    alt={`${property.title} ${i + 1}`}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
              </button>
            ))}
          </div>
          </div>
        </div>
        )}

        {lightbox && (
          <Lightbox
            images={lightboxItems}
            index={activeImage}
            onClose={() => setLightbox(false)}
            onNavigate={(idx) => setActiveImage(idx)}
            allowDownload={false}
          />
        )}

        {/* Property Details */}
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center text-charcoal mb-6">
              <MapPin className="w-5 h-5 mr-2 text-primary" />
              {location}
            </div>

            <div className="flex flex-wrap space-x-8 mb-8">
              <div className="flex items-center">
                <Bed className="w-6 h-6 text-primary mr-2" />
                <span className="text-charcoal">{property.bedrooms} Bedrooms</span>
              </div>
              <div className="flex items-center">
                <Bath className="w-6 h-6 text-primary mr-2" />
                <span className="text-charcoal">{property.bathrooms} Bathrooms</span>
              </div>
              {property.leasing_type && (
                <div className="flex items-center">
                  <KeyRound className="w-6 h-6 text-primary mr-2" />
                  <span className="text-charcoal capitalize">{property.leasing_type}</span>
                </div>
              )}
              {property.minimum_stay && (
                <div className="flex items-center">
                  <CalendarDays className="w-6 h-6 text-primary mr-2" />
                  <span className="text-charcoal">Min stay: {property.minimum_stay} days</span>
                </div>
              )}
            </div>

            <div className="prose max-w-none mb-8">
              <h2 className="text-2xl md:text-3xl font-semibold text-obsidian mb-4 capitalize">Description</h2>
              <p className="text-charcoal whitespace-pre-line">
                {property.listing_type === 'management' ? property.management_description : property.description}
              </p>
            </div>

            {property.features.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl md:text-3xl font-semibold text-obsidian mb-4 capitalize">Features</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {property.features.map((feature) => (
                    <div key={feature} className="flex items-center">
                      <Check className="w-5 h-5 text-primary mr-2 shrink-0" />
                      <span className="text-charcoal">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {property.sold_date && (
              <p className="text-sm text-taupe">Sold on {formatDate(property.sold_date)}</p>
            )}
          </div>

          <div>
            <div className="bg-white p-6 rounded-lg shadow-lg sticky top-4 border-t-4 border-primary">
              <p className="text-sm text-taupe mb-1">Price</p>
              <div className="text-3xl font-semibold text-obsidian mb-6">
                {formatPrice(property.price, property.currency)}
              </div>
              <form className="space-y-4" onSubmit={handleEnquiry}>
                {sent && <Alert type="success">Your enquiry has been sent. We'll be in touch shortly.</Alert>}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-charcoal mb-1">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={enquiry.name}
                    onChange={(e) => setEnquiry({ ...enquiry, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-charcoal mb-1">Email</label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={enquiry.email}
                    onChange={(e) => setEnquiry({ ...enquiry, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-charcoal mb-1">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    value={enquiry.phone}
                    onChange={(e) => setEnquiry({ ...enquiry, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-charcoal mb-1">Message</label>
                  <textarea
                    id="message"
                    rows={4}
                    value={enquiry.message}
                    onChange={(e) => setEnquiry({ ...enquiry, message: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="I'm interested in this property."
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-primary text-black py-2 font-semibold hover:bg-primary-light transition-colors disabled:opacity-50"
                >
                  {sending ? 'Sending...' : 'Contact Agent'}
                </button>
              </form>
            </div>
          </div>

          {/* Related Properties */}
          {property.related && property.related.length > 0 && (
            <div className="mt-16">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl md:text-3xl font-semibold text-obsidian capitalize">Related Properties</h2>
                <Link to={sectionHref} className="text-primary font-semibold text-sm hover:underline flex items-center whitespace-nowrap">
                  View all {sectionLabel.toLowerCase()} <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
                {property.related.map((p) => (
                  <PropertyCard key={p.id} property={p} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
  );
}
